var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useMemo } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowDropUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { DocumentProvider, useDocumentContext } from './reducer';
import { IconLoader } from '@themes/account/navigations/components';
// Screen reader only component for hidden text
const ScreenReaderOnly = ({ children }) => (_jsx("span", Object.assign({ style: {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        borderWidth: '0',
    } }, { children: children })));
const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
    color: theme.palette.text.secondary,
    // backgroundColor: 'red',
    [`& .${treeItemClasses.content}`]: {
        color: theme.palette.text.secondary,
        fontWeight: theme.typography.fontWeightMedium,
        '&.Mui-expanded': {
            fontWeight: theme.typography.fontWeightRegular,
        },
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
            backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
            color: 'var(--tree-view-color)',
        },
        [`& .${treeItemClasses.label}`]: {
            fontWeight: 'inherit',
            color: 'inherit',
        },
    },
    '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& img': {
        marginRight: 0,
    },
    '& .MuiTreeItem-iconContainer': {
        display: 'none !important',
    },
    // Improve focus visibility for keyboard navigation
    '&:focus-within': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
}));
const StyledTreeItem = forwardRef(function StyledTreeItem(props, ref) {
    const theme = useTheme();
    const { bgColor, color, labelIcon: LabelIcon, hasChildren, isExpanded, labelText, colorForDarkMode, bgColorForDarkMode, isFolder, fileType, fileUrl } = props, other = __rest(props, ["bgColor", "color", "labelIcon", "hasChildren", "isExpanded", "labelText", "colorForDarkMode", "bgColorForDarkMode", "isFolder", "fileType", "fileUrl"]);
    const styleProps = {
        '--tree-view-color': theme.palette.mode !== 'dark' ? color : colorForDarkMode,
        '--tree-view-bg-color': theme.palette.mode !== 'dark' ? bgColor : bgColorForDarkMode,
    };
    // Handle keyboard navigation for files
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            // Only navigate for files, not folders
            if (fileUrl && !hasChildren) {
                window.open(fileUrl, '_blank');
            }
        }
    };
    const itemType = isFolder ? 'folder' : fileType || 'file';
    const ariaLabel = isFolder ? `${labelText}, folder${isExpanded ? ', expanded' : ', collapsed'}` : `${labelText}, ${fileType || 'file'}`;
    return (_jsx(StyledTreeItemRoot, Object.assign({ label: _jsxs(Box, Object.assign({ sx: {
                display: 'flex',
                alignItems: 'center',
                p: 0.5,
                pr: 0,
                height: 40,
            }, onKeyDown: handleKeyDown, role: hasChildren ? 'treeitem' : 'link', "aria-expanded": hasChildren ? isExpanded : undefined, tabIndex: 0, "aria-label": ariaLabel }, { children: [LabelIcon && (_jsx(Box, Object.assign({ color: 'inherit', sx: { display: 'flex', alignItems: 'center', mr: 1, overflow: 'hidden' }, "aria-hidden": 'true' }, { children: LabelIcon }))), _jsx(Typography, Object.assign({ variant: 'body2', sx: { fontWeight: 'inherit', flexGrow: 1 } }, { children: labelText })), hasChildren && _jsx(Box, Object.assign({ "aria-hidden": 'true' }, { children: isExpanded ? _jsx(ArrowUpwardIcon, {}) : _jsx(ArrowDownwardIcon, {}) })), !hasChildren && fileUrl && _jsxs(ScreenReaderOnly, { children: ["Press Enter to open this ", fileType || 'file'] })] })), style: styleProps }, other, { ref: ref })));
});
function DocumentItem({ parentId, document }) {
    const [state] = useDocumentContext();
    const id = [parentId === null || parentId === void 0 ? void 0 : parentId.toString(), document.id.toString()].filter((cid) => cid).join('-');
    const handleClick = useCallback(() => {
        if (document.file_url) {
            window.open(document.file_url, '_blank');
        }
    }, [document.file_url]);
    const isExpanded = useMemo(() => state.expandedIds.includes(id), [state.expandedIds, id]);
    const fileType = useMemo(() => {
        var _a, _b, _c;
        if ((_a = document.file_url) === null || _a === void 0 ? void 0 : _a.includes('.pdf'))
            return 'PDF document';
        if ((_b = document.file_url) === null || _b === void 0 ? void 0 : _b.match(/\.(png|jpg|jpeg|gif|bmp|svg|webp)/i))
            return 'image';
        if ((_c = document.file_url) === null || _c === void 0 ? void 0 : _c.match(/^(http|https):\/\//i))
            return 'external link';
        return 'document';
    }, [document.file_url]);
    const isFolder = useMemo(() => (document.folder_icon && document.items.length > 0) || (!document.file_url && document.items.length > 0), [document.folder_icon, document.items, document.file_url]);
    const labelIcon = useMemo(() => {
        var _a, _b, _c;
        const fileIsPdf = !!((_a = document.file_url) === null || _a === void 0 ? void 0 : _a.includes('.pdf'));
        const fileIsImage = !!((_b = document.file_url) === null || _b === void 0 ? void 0 : _b.match(/\.(png|jpg|jpeg|gif|bmp|svg|webp)/i));
        const fileIsLink = !!((_c = document.file_url) === null || _c === void 0 ? void 0 : _c.match(/^(http|https):\/\//i));
        return document.icon ? (_jsx(IconLoader, { src: document.icon, alt: document.display_text })) : document.folder_icon && document.items.length ? (isExpanded ? (_jsx(FolderOpenIcon, { "aria-hidden": 'true', titleAccess: 'Open folder' })) : (_jsx(FolderIcon, { "aria-hidden": 'true', titleAccess: 'Closed folder' }))) : fileIsPdf ? (_jsx(PictureAsPdfIcon, { "aria-hidden": 'true', titleAccess: 'PDF document' })) : fileIsImage ? (_jsx(ImageIcon, { "aria-hidden": 'true', titleAccess: 'Image file' })) : fileIsLink ? (_jsx(LinkIcon, { "aria-hidden": 'true', titleAccess: 'External link' })) : null;
    }, [document.file_url, document.icon, document.display_text, document.folder_icon, document.items.length, isExpanded]);
    return (_jsx(StyledTreeItem, Object.assign({ nodeId: id, labelText: document.display_text || document.title, labelIcon: labelIcon, isExpanded: isExpanded, hasChildren: document.items.length > 0, isFolder: isFolder, fileType: fileType, fileUrl: document.file_url, onClick: handleClick }, { children: document.items.map((item) => (_jsx(DocumentItem, { document: item, parentId: document.id }, item.id))) }), id));
}
function DocumentTreeView({ documents }) {
    const [state, set] = useDocumentContext();
    const handleToggle = useCallback((event, nodeIds) => {
        set({ type: 'TOGGLE', ids: nodeIds });
        // Announce toggle to screen readers
        const announceElement = document.getElementById('document-tree-live-region');
        if (announceElement) {
            const expanded = nodeIds.length > state.expandedIds.length ? 'expanded' : 'collapsed';
            announceElement.textContent = `Folder ${expanded}`;
        }
    }, [set, state.expandedIds.length]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { id: 'document-tree-live-region', "aria-live": 'polite', role: 'status', style: {
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: '0',
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    borderWidth: '0',
                } }), _jsx(TreeView, Object.assign({ "aria-label": 'Document tree', expanded: state.expandedIds, onNodeToggle: handleToggle, sx: { flexGrow: 1, maxWidth: 1000, paddingX: 3, overflowY: 'auto' } }, { children: documents.map((document) => (_jsx(DocumentItem, { document: document }, document.id))) }))] }));
}
export default function Documents({ documents }) {
    return (_jsx(DocumentProvider, { children: _jsx(Box, Object.assign({ sx: { display: 'flex', justifyContent: 'center' }, role: 'region', "aria-label": 'Document browser' }, { children: _jsx(DocumentTreeView, { documents: documents }) })) }));
}
